import { useEffect, useState } from "react";
import { api } from "../../../api";
import { SectionCard } from "../../../components/SectionCard";
import { InfoRow } from "../../../components/InfoRow";
import { Button } from "../../../components/ui/button";
import { useLanguage } from "../../../contexts/LanguageContext";
import { settingsLabels } from "../labels";
import { navigate } from "../../../lib/router";
import { Building2 } from "lucide-react";
import { formatCpf, formatCnpj } from "../../utils/document";

type CompanyInfo = {
  id: number;
  name: string;
  cnpj?: string | null;
  cpf?: string | null;
  legal_name?: string | null;
  phone?: string | null;
  website?: string | null;
  email?: string | null;
  address?: string | null;
  plan?: string | null;
  next_renewal?: string | null;
  require_evolution?: boolean;
};

export function CompanyInfoCard() {
  const { lang } = useLanguage();
  const t = settingsLabels[lang].companyInfo;
  const [company, setCompany] = useState<CompanyInfo | null>(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const res = await api.getCompany();
    if (res.ok) setCompany(res.data);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <>
      <SectionCard
        header={
          <div className="flex items-center gap-2">
            <Building2 className="h-3.5 w-3.5 text-neutral-400" />
            {t.header}
          </div>
        }
        action={
          company ? (
            <Button 
              variant="ghost"
              onClick={() => navigate("/app/settings/company")}
              className="h-9 px-4 font-bold text-[10px] uppercase tracking-widest text-emerald-600 hover:text-white hover:bg-emerald-600 rounded-xl transition-all"
            >
              {t.edit}
            </Button>
          ) : undefined
        }
      >
        {loading || !company ? (
          <div>{t.loading}</div>
        ) : (
          <div className="grid gap-8 md:grid-cols-2">
            <div>
              <div className="mb-4 font-medium">{t.sections.info}</div>
              <div className="space-y-3">
                <InfoRow label={t.fields.name} value={company.name} />
                <InfoRow label={t.fields.legalName} value={company.legal_name} />
                <InfoRow
                  label={company.cnpj ? t.fields.cnpj : t.fields.cpf}
                  value={
                    company.cnpj
                      ? formatCnpj(company.cnpj)
                      : company.cpf
                      ? formatCpf(company.cpf)
                      : undefined
                  }
                />
                <InfoRow
                  label={t.fields.requireEvolution}
                  value={company.require_evolution !== false ? t.fields.yes : t.fields.no}
                />
              </div>
            </div>
            <div>
              <div className="mb-4 font-medium">{t.sections.contact}</div>
              <div className="space-y-3">
                <InfoRow label={t.fields.phone} value={company.phone} />
                <InfoRow label={t.fields.website} value={company.website} />
                <InfoRow label={t.fields.email} value={company.email} />
              </div>
            </div>
            {company.address && (
              <div className="md:col-span-2">
                <div className="mb-4 font-medium">{t.sections.address}</div>
                <div className="space-y-3">
                  <InfoRow label={t.fields.address} value={company.address} />
                </div>
              </div>
            )}
          </div>
        )}
      </SectionCard>
    </>
  );
}
